'use client';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Group, Box, Text, Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck } from '@tabler/icons-react';
import { useUser } from '@/context/UserContext';
import {
  fetchProgress as fetchServerProgress,
  saveProgress as saveServerProgress,
  deleteProgress as deleteServerProgress,
} from '@/lib/account';
import { getThumbUrl } from '@/lib/imageUrl';
import { trackEvent } from '@/lib/telemetry';
import { fireBagConfetti, fireSetConfetti } from '@/lib/confetti';

// Alle check-voortgang van een set in één hook: localStorage-persistentie,
// account-sync (last-write-wins), undo-toasts, confetti en de homepage-
// samenvatting. Eén-op-één uit SetPageClient getrokken zodat de overzichts-
// pagina en de bouwmodus dezelfde waarheid delen — beide schermen lezen en
// schrijven dezelfde localStorage-keys, wisselen kost dus niets.
//
// `active` bepaalt of viermomenten (confetti, completion-events) afgaan:
// de overzichtspagina geeft checkMode door, de sort mode `true`.
export function useSortProgress(id, set, { active = true } = {}) {
  const { account, showNewBadges } = useUser();
  const accountId = account?.id;

  const [checkedParts, setCheckedParts] = useState({});

  useEffect(() => {
    if (id) {
      const stored = localStorage.getItem(`bricksperbag_check_${id}`);
      if (stored) {
        try { setCheckedParts(JSON.parse(stored)); } catch (e) { console.error('Error loading check state:', e); setCheckedParts({}); }
      } else {
        setCheckedParts({});
      }
    }
  }, [id]);

  // Progress-sync (ingelogd): neem servervoortgang over als die nieuwer is dan
  // wat er lokaal staat — last-write-wins op de updatedAt-timestamp. Pas na
  // deze reconciliatie mag er gepusht worden: een push stempelt Date.now(),
  // dus stale lokale data zou anders nieuwere serverdata overschrijven.
  const [progressSyncReady, setProgressSyncReady] = useState(false);
  useEffect(() => {
    if (!id || !accountId) { setProgressSyncReady(false); return; }
    let cancelled = false;
    setProgressSyncReady(false);
    (async () => {
      try {
        const server = await fetchServerProgress(id);
        if (cancelled) return;
        if (server?.progress) {
          const serverTs = new Date(server.updated_at).getTime();
          let localTs = 0;
          try {
            const summary = JSON.parse(localStorage.getItem(`bricksperbag_progress_${id}`) || 'null');
            localTs = summary?.updatedAt || 0;
          } catch (e) { /* corrupt summary → server wint */ }
          if (serverTs > localTs) {
            setCheckedParts(server.progress);
            try { localStorage.setItem(`bricksperbag_check_${id}`, JSON.stringify(server.progress)); } catch (e) { /* localStorage unavailable */ }
          }
        }
      } catch (e) { /* offline — lokale voortgang blijft leidend */ }
      if (!cancelled) setProgressSyncReady(true);
    })();
    return () => { cancelled = true; };
  }, [id, accountId]);

  // Debounced save: JSON.stringify of the whole check state on every tap adds
  // latency to the core interaction. Trailing 400ms write + flush on unmount.
  const checkedPartsRef = useRef(checkedParts);
  useEffect(() => {
    checkedPartsRef.current = checkedParts;
    if (!id || Object.keys(checkedParts).length === 0) return;
    const timer = setTimeout(() => {
      try {
        localStorage.setItem(`bricksperbag_check_${id}`, JSON.stringify(checkedParts));
      } catch (e) { /* localStorage unavailable */ }
    }, 400);
    return () => clearTimeout(timer);
  }, [id, checkedParts]);
  useEffect(() => () => {
    // Flush a pending write when leaving the page so the last taps aren't lost
    if (id && Object.keys(checkedPartsRef.current).length > 0) {
      try {
        localStorage.setItem(`bricksperbag_check_${id}`, JSON.stringify(checkedPartsRef.current));
      } catch (e) { /* localStorage unavailable */ }
    }
  }, [id]);

  // Undo-vangnet: een long-press-complete zet in één keer meerdere stuks om —
  // de toast maakt dat één tik omkeerbaar zonder de flow te vertragen. Het
  // plaatje + id maken duidelijk wélk onderdeel het was, zodat je bij
  // meerdere gestapelde toasts de juiste kunt terugdraaien.
  const showUndoToast = useCallback((partKey, previousCount, quantity, part) => {
    const toastId = `check-undo-${partKey}`;
    const undo = () => {
      notifications.hide(toastId);
      setCheckedParts(prev => {
        const next = { ...prev };
        if (previousCount <= 0) { delete next[partKey]; } else { next[partKey] = previousCount; }
        return next;
      });
    };
    notifications.show({
      id: toastId,
      color: 'green',
      autoClose: 4000,
      withCloseButton: false,
      message: (
        <Group wrap="nowrap" gap="sm">
          {part?.imageUrl ? (
            <img
              src={getThumbUrl(part.imageUrl, 96)}
              alt={part.name || ''}
              style={{ width: 36, height: 36, objectFit: 'contain', mixBlendMode: 'multiply', flexShrink: 0 }}
            />
          ) : (
            <IconCheck size={18} color="#2f9e44" style={{ flexShrink: 0 }} />
          )}
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text size="sm" fw={600}>{quantity === 1 ? 'Piece checked off' : `All ${quantity} pieces checked off`}</Text>
            {part && (
              <Text size="xs" c="dimmed" lineClamp={1}>
                {part.partId}{part.colorName ? ` · ${part.colorName}` : ''}
              </Text>
            )}
          </Box>
          <Button size="compact-xs" variant="light" onClick={undo} style={{ flexShrink: 0 }}>
            Undo
          </Button>
        </Group>
      ),
    });
  }, []);

  const handleToggleCheck = useCallback((partKey, quantity, action = 'increment', part = null) => {
    if (action === 'complete') {
      const cur = checkedPartsRef.current[partKey];
      const current = typeof cur === 'number' ? cur : (cur ? quantity : 0);
      if (current < quantity) showUndoToast(partKey, current, quantity, part);
    }
    setCheckedParts(prev => {
      const newState = { ...prev };
      const current = typeof newState[partKey] === 'number' ? newState[partKey] : (newState[partKey] ? quantity : 0);
      if (action === 'complete') {
        newState[partKey] = quantity;
      } else if (action === 'reset') {
        delete newState[partKey];
      } else if (action === 'decrement') {
        const next = current - 1;
        if (next <= 0) { delete newState[partKey]; } else { newState[partKey] = next; }
      } else {
        // increment
        const next = Math.min(current + 1, quantity);
        newState[partKey] = next;
      }
      return newState;
    });
  }, [showUndoToast]);

  const handleResetBag = useCallback((bagNumber) => {
    if (!set?.bags) return;
    const bag = set.bags.find(b => b.bagNumber === bagNumber);
    if (!bag?.parts) return;
    setCheckedParts(prev => {
      const newState = { ...prev };
      bag.parts.forEach(part => {
        const key = `${bag.id}_${part.partId}_${part.color_id || 'nocolor'}`;
        delete newState[key];
      });
      return newState;
    });
  }, [set]);

  const handleResetAll = useCallback(() => {
    setCheckedParts({});
    localStorage.removeItem(`bricksperbag_check_${id}`);
    // Ook de gesyncte voortgang op het account wissen
    if (accountId) deleteServerProgress(id).catch(() => {});
    trackEvent('Check Reset', { set: id });
  }, [id, accountId]);

  const totalCheckProgress = useMemo(() => {
    if (!set?.bags) return { checked: 0, total: 0, percentage: 0 };
    let checked = 0;
    let total = 0;
    set.bags.forEach(bag => {
      if (bag.bagNumber === '0') return;
      // De "Left over"-stapel van een build is een optionele extra controle en
      // telt niet mee: 100% = de build zelf is klaar om te bouwen
      if (bag.leftover) return;
      if (!bag.parts) return;
      bag.parts.forEach(part => {
        const key = `${bag.id}_${part.partId}_${part.color_id || 'nocolor'}`;
        total += part.quantity || 0;
        const count = typeof checkedParts[key] === 'number' ? checkedParts[key] : (checkedParts[key] ? (part.quantity || 0) : 0);
        checked += Math.min(count, part.quantity || 0);
      });
    });
    return { checked, total, percentage: total > 0 ? (checked / total) * 100 : 0 };
  }, [set, checkedParts]);

  // Per-bag completion percentage (for green quick-jump numbers in check mode)
  const bagCompletion = useMemo(() => {
    if (!set?.bags) return {};
    const map = {};
    set.bags.forEach(bag => {
      if (!bag.parts || bag.parts.length === 0) return;
      let total = 0;
      let checked = 0;
      bag.parts.forEach(part => {
        const key = `${bag.id}_${part.partId}_${part.color_id || 'nocolor'}`;
        total += part.quantity || 0;
        const count = typeof checkedParts[key] === 'number' ? checkedParts[key] : (checkedParts[key] ? (part.quantity || 0) : 0);
        checked += Math.min(count, part.quantity || 0);
      });
      map[bag.bagNumber] = total > 0 ? (checked / total) * 100 : 0;
    });
    return map;
  }, [set, checkedParts]);

  // Celebrate completion: a small LEGO-confetti burst when a bag hits 100%,
  // a big one when the whole set is done. Refs start at null so restoring a
  // saved check state on mount never fires confetti.
  const prevSetPct = useRef(null);
  useEffect(() => {
    const pct = totalCheckProgress.percentage;
    if (active && prevSetPct.current !== null && prevSetPct.current < 100 && pct >= 100) {
      fireSetConfetti();
      trackEvent('Check Set Completed', { set: id });
    }
    prevSetPct.current = pct;
  }, [totalCheckProgress.percentage, active, id]);

  const prevBagCompletion = useRef(null);
  useEffect(() => {
    const prev = prevBagCompletion.current;
    prevBagCompletion.current = bagCompletion;
    if (!prev || !active) return;
    let completedBag = false;
    for (const [bagNumber, pct] of Object.entries(bagCompletion)) {
      if (prev[bagNumber] !== undefined && prev[bagNumber] < 100 && pct >= 100) {
        completedBag = true;
        trackEvent('Check Bag Completed', { set: id, bag: bagNumber });
      }
    }
    // The whole-set burst already covers the last bag.
    if (completedBag && totalCheckProgress.percentage < 100) {
      fireBagConfetti();
    }
  }, [bagCompletion, active, totalCheckProgress.percentage, id]);

  // Save a check-progress summary so the homepage can show a "Continue checking"
  // card. Debounced: this fires on every tap otherwise.
  useEffect(() => {
    if (!set || !id || totalCheckProgress.total === 0) return;
    const timer = setTimeout(() => {
      try {
        const key = `bricksperbag_progress_${id}`;
        if (totalCheckProgress.checked === 0) {
          localStorage.removeItem(key);
        } else {
          localStorage.setItem(key, JSON.stringify({
            id,
            name: set.name,
            image_url: set.image_url,
            checked: totalCheckProgress.checked,
            total: totalCheckProgress.total,
            updatedAt: Date.now(),
          }));
        }
      } catch (e) { /* localStorage unavailable */ }
    }, 400);
    return () => clearTimeout(timer);
  }, [set, id, totalCheckProgress]);

  // Progress-sync (ingelogd): push de voortgang debounced naar het account.
  // Alleen bij niet-lege voortgang — een lege initiële state mag de server
  // nooit overschrijven; volledig resetten loopt via handleResetAll.
  useEffect(() => {
    if (!id || !accountId || !progressSyncReady || totalCheckProgress.total === 0) return;
    if (Object.keys(checkedParts).length === 0) return;
    const timer = setTimeout(async () => {
      try {
        const result = await saveServerProgress(id, {
          progress: checkedParts,
          checked: totalCheckProgress.checked,
          total: totalCheckProgress.total,
          updatedAt: Date.now(),
        });
        if (result?.newBadges?.length) showNewBadges(result.newBadges);
      } catch (e) { /* offline — volgende wijziging probeert opnieuw */ }
    }, 1500);
    return () => clearTimeout(timer);
  }, [id, accountId, progressSyncReady, checkedParts, totalCheckProgress, showNewBadges]);

  return {
    checkedParts,
    totalCheckProgress,
    bagCompletion,
    handleToggleCheck,
    handleResetBag,
    handleResetAll,
  };
}
